import {Injectable} from '@angular/core';
import {AuthService} from './auth.service';
import {Intercom} from 'ng-intercom';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {catchError, mergeMap} from 'rxjs/operators';
import {combineLatest, EMPTY, from, of} from 'rxjs';
import {environment} from '../../environments/environment';

export enum THEME {
  light = 'light',
  dark = 'dark'
}

export type SupportTarget = 'rejection-reason' | 'support-contact' | 'track-status';

/** Routing context on the support_context CDA; the Erro - LS Workflow branches on erro-ls. */
export type SupportContext = 'erro-ls' | '';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class SupportChatService {
  private _theme: THEME = THEME.light;
  public THEMES = THEME;

  constructor(private authService: AuthService,
              private intercom: Intercom) {}

  public init(): void {
    this.authService.user$.pipe(
      untilDestroyed(this),
      mergeMap(user => {
        if (!user) {
          return of([]);
        }
        const claims$ = from(user?.getIdTokenResult().then((tr) => tr?.claims as any));
        const userHash$ = this.authService.getUserHmacHash('intercom');
        // Per sign-in: a refused hash (an expired session) must not end the
        // subscription, or Intercom never boots for the next sign-in.
        return combineLatest([of(user.uid), claims$, userHash$]).pipe(catchError(() => EMPTY));
      }),
      catchError(() => {
        return [];
      })
    ).subscribe(([uid, claims, hash]) => {
      this.intercom.boot({
        app_id: environment.intercomAppId,
        user_id: uid || undefined,
        user_hash: hash,
        name: uid || undefined,
        app_name: 'Funcionario',
        app_version: environment.version,
        company: {
          id: claims?.company_id || undefined,
          name: claims?.company_id || undefined
        },
        hide_default_launcher: true,
        custom_launcher_selector: '#chatButton',
        widget: {
          activator: '#intercom'
        }
      });
    });
  }

  public setTheme(t: THEME): void {
    setTimeout(() => {
      this._theme = t;
    });
  }

  public get theme(): THEME {
    return this._theme;
  }

  /**
   * Opens Intercom support for a status-screen CTA:
   *  - support-contact -> a new message composer ("Envie uma mensagem")
   *  - track-status / rejection-reason -> the matching Help Center article
   *
   * ng-intercom 8 has no showArticle(), so articles go through the global Intercom JS
   * API. Article ids live in the environment (per workspace). Falls back to opening the
   * messenger when an id is missing/invalid.
   */
  public openSupport(target: SupportTarget): void {
    if (target === 'support-contact') {
      this.intercom.showNewMessage();
      return;
    }
    this.showArticle(environment.intercomArticles?.[target]);
  }

  private showArticle(articleId?: string): void {
    const id = Number(articleId);
    const intercomGlobal = (window as unknown as {
      Intercom?: (action: string, arg?: unknown) => void;
    }).Intercom;
    if (articleId && Number.isFinite(id) && typeof intercomGlobal === 'function') {
      intercomGlobal('showArticle', id);
    } else {
      this.intercom.show();
    }
  }

  /**
   * INVARIANT: the routing Workflow must trigger on "sends their first message", not
   * "opens a new conversation" — support_context is written at chat-open and only
   * propagates before an evaluation that happens after the user sends. It is a persisted
   * CDA, so a neutral open must reset it to '' to avoid misrouting a later conversation.
   */
  public setSupportContext(context: SupportContext): void {
    this.intercom.update({support_context: context});
  }
}
